import {
  fmtDateInput,
  nyDateInputToMs,
  nyStartOfDayMs,
} from '@/lib/datetime';
import type { Instant } from '@/lib/datetime';
import type { PerfHaloMetric as WirePerfHaloMetric } from '@/api/types';
import {
  PERF_HALO_METRIC_IDS as REPORT_METRIC_IDS,
} from '@/lib/perf-halo-metrics';
import type {
  PerfHaloMetricId as ReportMetricId,
  PerfHaloSortDirection as MetricSortDirection,
  PerfHaloSortField as MetricSortField,
} from '@/lib/perf-halo-metrics';

/**
 * Complete, reproducible state for the analyst-facing Perf + Halo report.
 *
 * Dataset, all-effects attribution, and USD are report invariants rather than
 * URL choices. Keeping them out of the search object prevents a stale link
 * from silently weakening the report's attribution contract.
 */
export const PERF_HALO_REPORT_TAB_IDS = [
  'overview',
  'campaign',
  'production',
  'channel',
  'audience',
  'audienceType',
  'creative',
  'haloAnalysis',
] as const;

export const PERF_HALO_TREND_METRIC_IDS = REPORT_METRIC_IDS;

export const PERF_HALO_CHANNEL_MIX_METRIC_IDS = [
  'gross_spend',
  'attributed_conversions',
  'attributed_revenue',
] as const;

export const PERF_HALO_METRIC_IDS = [
  'tickets',
  'ticket_value',
] as const satisfies readonly WirePerfHaloMetric[];
export const PERF_HALO_SORT_FIELD_IDS = REPORT_METRIC_IDS;
export const PERF_HALO_SORT_DIRECTION_IDS = ['asc', 'desc'] as const;
export const PERF_HALO_TARGET_IDS = ['meta', 'ttd'] as const;
export const PERF_HALO_CHANNEL_IDS = ['meta', 'ctv', 'dsp'] as const;
export const PERF_HALO_AUDIENCE_TYPE_IDS = [
  'direct',
  'lookalike',
  'retargeting',
] as const;

export type PerfHaloReportTab = (typeof PERF_HALO_REPORT_TAB_IDS)[number];
export type PerfHaloTrendMetric = ReportMetricId;
export type PerfHaloChannelMixMetric =
  (typeof PERF_HALO_CHANNEL_MIX_METRIC_IDS)[number];
export type PerfHaloMetric = WirePerfHaloMetric;
export type PerfHaloSortField = MetricSortField;
export type PerfHaloSortDirection = MetricSortDirection;
export type PerfHaloTarget = (typeof PERF_HALO_TARGET_IDS)[number];
export type PerfHaloChannel = (typeof PERF_HALO_CHANNEL_IDS)[number];
export type PerfHaloAudienceType =
  (typeof PERF_HALO_AUDIENCE_TYPE_IDS)[number];

export interface PerfHaloReportSearch {
  tab: PerfHaloReportTab;
  /** Inclusive, literal America/New_York calendar days. */
  from: string;
  to: string;
  trendMetric: PerfHaloTrendMetric;
  channelMixMetric: PerfHaloChannelMixMetric;
  haloMetric: PerfHaloMetric;
  productionId?: string;
  campaignId?: string;
  target?: PerfHaloTarget;
  channel?: PerfHaloChannel;
  /** Audience is a revisioned identity: both fields are present or neither is. */
  audienceId?: string;
  audienceRevision?: number;
  audienceType?: PerfHaloAudienceType;
  creativeId?: string;
  sortField: PerfHaloSortField;
  sortDirection: PerfHaloSortDirection;
}

export type PerfHaloReportSearchPatch = Partial<PerfHaloReportSearch>;
export type SerializedPerfHaloReportSearch = PerfHaloReportSearch;

export const DEFAULT_PERF_HALO_REPORT_TAB: PerfHaloReportTab = 'overview';
export const DEFAULT_PERF_HALO_TREND_METRIC: PerfHaloTrendMetric = 'gross_spend';
export const DEFAULT_PERF_HALO_CHANNEL_MIX_METRIC: PerfHaloChannelMixMetric =
  'attributed_revenue';
export const DEFAULT_PERF_HALO_METRIC: PerfHaloMetric = 'ticket_value';
export const DEFAULT_PERF_HALO_SORT_FIELD: PerfHaloSortField =
  'attributed_revenue';
export const DEFAULT_PERF_HALO_SORT_DIRECTION: PerfHaloSortDirection = 'desc';

const DAY_MS = 86_400_000;
const HALF_DAY_MS = DAY_MS / 2;
const DEFAULT_COMPLETED_DAYS = 30;
const MAX_RANGE_DAYS = 366;
const MAX_REVISION = 4_294_967_295;

const SLUG_ID = /^[a-z0-9_-]{1,64}$/;
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const PRODUCTION_ID = new RegExp(`^(?:${UUID}|Production/${UUID})$`);

interface DateRange {
  from: string;
  to: string;
}

/** Last 30 fully completed New York days; today's provisional day is opt-in. */
export function defaultPerfHaloReportDateRange(now: Instant = Date.now()): DateRange {
  const todayStart = nyStartOfDayMs(now);
  let fromStart = todayStart;
  for (let day = 0; day < DEFAULT_COMPLETED_DAYS; day += 1) {
    // Noon-ish on the preceding NY day remains stable across DST transitions.
    fromStart = nyStartOfDayMs(fromStart - HALF_DAY_MS);
  }
  return {
    from: fmtDateInput(fromStart),
    to: fmtDateInput(todayStart - 1),
  };
}

/** Total validator used by both the canonical route and legacy redirect. */
export function parsePerfHaloReportSearch(
  search: object,
  now: Instant = Date.now(),
): PerfHaloReportSearch {
  const raw = search as Record<string, unknown>;
  const fallbackRange = defaultPerfHaloReportDateRange(now);
  const range = parseDateRange(raw.from, raw.to) ?? fallbackRange;
  const audienceId = parseSlugId(raw.audienceId ?? raw.audience);
  const audienceRevision = parseRevision(raw.audienceRevision ?? raw.revision);
  const hasAudienceIdentity = audienceId !== undefined && audienceRevision !== undefined;

  return {
    tab: parseEnum(raw.tab, PERF_HALO_REPORT_TAB_IDS, DEFAULT_PERF_HALO_REPORT_TAB),
    ...range,
    trendMetric: parseEnum(
      raw.trendMetric ?? raw.trend,
      PERF_HALO_TREND_METRIC_IDS,
      DEFAULT_PERF_HALO_TREND_METRIC,
    ),
    channelMixMetric: parseEnum(
      raw.channelMixMetric ?? raw.mix,
      PERF_HALO_CHANNEL_MIX_METRIC_IDS,
      DEFAULT_PERF_HALO_CHANNEL_MIX_METRIC,
    ),
    haloMetric: parseEnum(
      raw.haloMetric ?? raw.halo,
      PERF_HALO_METRIC_IDS,
      DEFAULT_PERF_HALO_METRIC,
    ),
    productionId: parseProductionId(raw.productionId ?? raw.production),
    campaignId: parseSlugId(raw.campaignId ?? raw.campaign),
    target: parseOptionalEnum(raw.target ?? raw.destination, PERF_HALO_TARGET_IDS),
    channel: parseOptionalEnum(raw.channel, PERF_HALO_CHANNEL_IDS),
    audienceId: hasAudienceIdentity ? audienceId : undefined,
    audienceRevision: hasAudienceIdentity ? audienceRevision : undefined,
    audienceType: parseOptionalEnum(
      raw.audienceType ?? raw.audience_type,
      PERF_HALO_AUDIENCE_TYPE_IDS,
    ),
    creativeId: parseSlugId(raw.creativeId ?? raw.creative),
    sortField: parseEnum(
      raw.sortField ?? raw.sort,
      PERF_HALO_SORT_FIELD_IDS,
      DEFAULT_PERF_HALO_SORT_FIELD,
    ),
    sortDirection: parseEnum(
      raw.sortDirection ?? raw.direction,
      PERF_HALO_SORT_DIRECTION_IDS,
      DEFAULT_PERF_HALO_SORT_DIRECTION,
    ),
  };
}

/** Write all behavioral defaults and dates for deterministic shared links. */
export function serializePerfHaloReportSearch(
  search: PerfHaloReportSearch,
): SerializedPerfHaloReportSearch {
  return { ...search };
}

/** Apply a UI change without losing any unrelated URL-controlled report state. */
export function updatePerfHaloReportSearch(
  current: PerfHaloReportSearch,
  patch: PerfHaloReportSearchPatch,
): PerfHaloReportSearch {
  const candidate = { ...current, ...patch };
  const range = parseDateRange(candidate.from, candidate.to) ?? {
    from: current.from,
    to: current.to,
  };
  const audienceChangedWithoutRevision =
    Object.prototype.hasOwnProperty.call(patch, 'audienceId')
    && patch.audienceId !== current.audienceId
    && !Object.prototype.hasOwnProperty.call(patch, 'audienceRevision');
  const audienceId = parseSlugId(candidate.audienceId);
  const audienceRevision = audienceChangedWithoutRevision
    ? undefined
    : parseRevision(candidate.audienceRevision);
  const hasAudienceIdentity = audienceId !== undefined && audienceRevision !== undefined;

  return {
    tab: parseEnum(candidate.tab, PERF_HALO_REPORT_TAB_IDS, current.tab),
    ...range,
    trendMetric: parseEnum(
      candidate.trendMetric,
      PERF_HALO_TREND_METRIC_IDS,
      current.trendMetric,
    ),
    channelMixMetric: parseEnum(
      candidate.channelMixMetric,
      PERF_HALO_CHANNEL_MIX_METRIC_IDS,
      current.channelMixMetric,
    ),
    haloMetric: parseEnum(
      candidate.haloMetric,
      PERF_HALO_METRIC_IDS,
      current.haloMetric,
    ),
    productionId: parseProductionId(candidate.productionId),
    campaignId: parseSlugId(candidate.campaignId),
    target: parseOptionalEnum(candidate.target, PERF_HALO_TARGET_IDS),
    channel: parseOptionalEnum(candidate.channel, PERF_HALO_CHANNEL_IDS),
    audienceId: hasAudienceIdentity ? audienceId : undefined,
    audienceRevision: hasAudienceIdentity ? audienceRevision : undefined,
    audienceType: parseOptionalEnum(
      candidate.audienceType,
      PERF_HALO_AUDIENCE_TYPE_IDS,
    ),
    creativeId: parseSlugId(candidate.creativeId),
    sortField: parseEnum(
      candidate.sortField,
      PERF_HALO_SORT_FIELD_IDS,
      current.sortField,
    ),
    sortDirection: parseEnum(
      candidate.sortDirection,
      PERF_HALO_SORT_DIRECTION_IDS,
      current.sortDirection,
    ),
  };
}

function parseDateRange(from: unknown, to: unknown): DateRange | undefined {
  const parsedFrom = parseCalendarDay(from);
  const parsedTo = parseCalendarDay(to);
  if (!parsedFrom || !parsedTo || parsedFrom.ms > parsedTo.ms) return undefined;
  const inclusiveDays = Math.round((parsedTo.ms - parsedFrom.ms) / DAY_MS) + 1;
  if (inclusiveDays > MAX_RANGE_DAYS) return undefined;
  return { from: parsedFrom.value, to: parsedTo.value };
}

function parseCalendarDay(value: unknown): { value: string; ms: number } | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  const ms = nyDateInputToMs(normalized);
  if (!Number.isFinite(ms) || fmtDateInput(ms) !== normalized) return undefined;
  return { value: normalized, ms };
}

function parseSlugId(value: unknown): string | undefined {
  return typeof value === 'string' && SLUG_ID.test(value) ? value : undefined;
}

function parseProductionId(value: unknown): string | undefined {
  return typeof value === 'string' && PRODUCTION_ID.test(value) ? value : undefined;
}

function parseRevision(value: unknown): number | undefined {
  const parsed = typeof value === 'number'
    ? value
    : typeof value === 'string' && /^[1-9][0-9]*$/.test(value)
      ? Number(value)
      : Number.NaN;
  return Number.isSafeInteger(parsed) && parsed > 0 && parsed <= MAX_REVISION
    ? parsed
    : undefined;
}

function parseEnum<const T extends readonly string[]>(
  value: unknown,
  values: T,
  fallback: T[number],
): T[number] {
  return typeof value === 'string' && values.includes(value)
    ? (value as T[number])
    : fallback;
}

function parseOptionalEnum<const T extends readonly string[]>(
  value: unknown,
  values: T,
): T[number] | undefined {
  return typeof value === 'string' && values.includes(value)
    ? (value as T[number])
    : undefined;
}
